import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { adminApi, ApiError } from '../services/adminApi';

const AuthContext = createContext(null);

const getFingerprint = () => {
  if (typeof window === 'undefined') return '';
  const nav = window.navigator || {};
  const parts = [
    nav.userAgent || '',
    nav.language || '',
    window.screen ? `${window.screen.width}x${window.screen.height}` : '',
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
  ];
  return btoa(parts.join('|'));
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [usageRemaining, setUsageRemaining] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const storedToken =
      typeof window !== 'undefined'
        ? window.localStorage.getItem('cfp_token')
        : null;
    if (storedToken) {
      setToken(storedToken);
    }
    adminApi
      .getSession(storedToken || undefined)
      .then((res) => {
        if (!active) return;
        if (res?.user) {
          setUser(res.user);
        }
      })
      .catch(() => {
        if (!active) return;
        if (storedToken && typeof window !== 'undefined') {
          window.localStorage.removeItem('cfp_token');
        }
        setToken(null);
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    const fingerprint = getFingerprint();
    const payload = await adminApi.login(email, password, fingerprint);
    
    // Save to state
    setToken(payload.token);
    setUser(payload.user);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('cfp_token', payload.token);
    }
    
    return payload;
  }, []);

  const logout = useCallback(async () => {
    try {
      await adminApi.logout(token);
    } catch {
      // ignore
    }
    
    // Clear state
    setToken(null);
    setUser(null);
    setUsageRemaining(null);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('cfp_token');
    }
  }, [token]);

  const recordSearch = useCallback(
    async (term, type) => {
      if (!user) return null;
      try {
        const res = await adminApi.logSearch({ term, type }, token);
        if (typeof res?.remaining === 'number') {
          setUsageRemaining(res.remaining);
        }
        return res;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          await logout();
        }
        // Swallow search-log failures so search continues even if logging fails
        console.warn('Search log failed', err);
        return null;
      }
    },
    [token, user, logout]
  );

  const value = useMemo(
    () => ({
      token,
      user,
      usageRemaining,
      isAuthenticated: Boolean(user || token),
      isLoading,
      login,
      logout,
      recordSearch,
    }),
    [token, user, usageRemaining, isLoading, login, logout, recordSearch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
